import { useState } from "react";
import Navbar from "Components/Navbar/Navbar";
// import Footer from "Components/Footer/Footer";
import { Button, Card, Typography, Space, Divider, Form, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { normalizePhoneNumber } from "./signup.service";
import { sendLoginOtp, loginPortal, selectAccount } from "./login.service";
import AccountPicker from "./_blocks/AccountPicker";

const { Title, Text } = Typography;

function Login() {
  const navigate = useNavigate();
  const setUserData = useAuthStore((s) => s.setUserData);
  const [form] = Form.useForm();
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [accountSelection, setAccountSelection] = useState(null);
  const [selectingId, setSelectingId] = useState(null);

  const applyAuthResult = (data) => {
    if (!data?.token && data?.account_info?.account_id) {
      renderSuccessNotifications({
        title: "Account Under Review",
        message: data?.message || "Your account is still pending approval.",
      });
      navigate(
        `/application-status?account_id=${data?.account_info?.account_id}`
      );
      return;
    }

    setUserData({ user: data.account_info, token: data.token });
    renderSuccessNotifications({
      title: "Signed in",
      message: data?.message || "Welcome back!",
    });
    navigate("/shoup-community/dashboard", { replace: true });
  };

  const handleSelectAccount = async (account) => {
    try {
      setSelectingId(account.account_id);
      const { data, errors } = await selectAccount({
        account_id: account.account_id,
        selection_token: accountSelection.selectionToken,
      });
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }
      applyAuthResult(data);
    } finally {
      setSelectingId(null);
    }
  };

  const handleSendOtp = async () => {
    try {
      const values = await form.validateFields(["phone"]);
      setSendingOtp(true);
      const { errors } = await sendLoginOtp(values.phone);
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }
      setOtpSent(true);
      renderSuccessNotifications({
        title: "OTP sent",
        message: "Check your phone for the code.",
      });
    } catch (err) {
      if (err?.errorFields) return;
      renderErrorNotifications([
        { message: err?.message || "Failed to send OTP. Please try again." },
      ]);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleLogin = async () => {
    try {
      const values = await form.validateFields(["phone", "otp"]);
      setLoggingIn(true);
      const { data, errors } = await loginPortal({
        phone_number: values.phone,
        otp: values.otp.trim(),
      });
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }

      if (data?.needs_account_selection) {
        setAccountSelection({
          selectionToken: data.selection_token,
          accounts: data.accounts ?? [],
        });
        return;
      }

      applyAuthResult(data);
    } catch (err) {
      if (err?.errorFields) return;
      renderErrorNotifications([
        {
          message:
            err?.message || "Sign in failed. Check your OTP and try again.",
        },
      ]);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const phone = form.getFieldValue("phone");
      if (!phone?.trim()) {
        renderErrorNotifications([
          { message: "Enter your phone number first." },
        ]);
        return;
      }
      setSendingOtp(true);
      const { errors } = await sendLoginOtp(phone);
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }
      renderSuccessNotifications({
        title: "OTP resent",
        message: "A new code has been sent to your phone.",
      });
    } catch (err) {
      renderErrorNotifications([
        { message: err?.message || "Failed to resend OTP." },
      ]);
    } finally {
      setSendingOtp(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex flex-1 items-center justify-center p-4 bg-gradient-shoup">
        <div className="w-full max-w-md space-y-6 animate-in">
          <div className="space-y-2 text-center">
            <Title
              level={2}
              className="!mb-0 !text-hero !tracking-tight !uppercase"
            >
              Welcome back
            </Title>
            <Text type="secondary" className="text-base">
              {accountSelection
                ? "Pick the account you want to sign in to."
                : "Sign in with your phone number and a one-time code."}
            </Text>
          </div>
          <Card className="rounded-3xl border-border/30 bg-card/90 shadow-elevated backdrop-blur-sm">
            <Space direction="vertical" size="large" className="w-full">
              {!accountSelection && (
                <div className="flex justify-center">
                  <img
                    src="/shoup.svg"
                    alt="ShoUp"
                    className="h-16 w-16 rounded-2xl"
                  />
                </div>
              )}
              {accountSelection ? (
                <AccountPicker
                  accounts={accountSelection.accounts}
                  onSelect={handleSelectAccount}
                  selectingId={selectingId}
                />
              ) : (
              <Form
                form={form}
                layout="vertical"
                className="w-full"
                onFinish={otpSent ? handleLogin : handleSendOtp}
              >
                <Form.Item
                  name="phone"
                  label={
                    <>
                      Phone number <span className="text-destructive">*</span>
                    </>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        const digits = normalizePhoneNumber(value || "");
                        if (digits.length !== 10) {
                          return Promise.reject(
                            new Error("Enter a valid phone number (10 digits).")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    className="rounded-xl"
                    maxLength={10}
                    minLength={10}
                  />
                </Form.Item>
                {otpSent && (
                  <Form.Item
                    name="otp"
                    normalize={(value) => value?.replace(/\D/g, "")}
                    rules={[
                      {
                        required: true,
                        message: "Enter the OTP you received.",
                      },
                      {
                        pattern: /^\d{6}$/,
                        message: "OTP should be 6 digits.",
                      },
                    ]}
                  >
                    <Input
                      maxLength={6}
                      inputMode="numeric"
                      placeholder="123456"
                    />
                  </Form.Item>
                )}
                <Button
                  type="primary"
                  size="large"
                  block
                  htmlType="submit"
                  loading={otpSent ? loggingIn : sendingOtp}
                >
                  {otpSent ? "Sign in" : "Send OTP"}
                </Button>
                {otpSent && (
                  <Button
                    type="link"
                    className="!p-0 h-auto"
                    disabled={sendingOtp}
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                  </Button>
                )}
              </Form>
              )}
              <Divider plain className="!my-0">
                New to ShoUp?
              </Divider>
              <div className="text-center">
                <Text type="secondary" className="text-sm">
                  Create a partner account to apply and access the dashboard.
                </Text>
                <div className="mt-3">
                  <Link to="/signup" className="block">
                    <Button type="default" size="large" block>
                      Register for a new account
                    </Button>
                  </Link>
                </div>
              </div>
            </Space>
          </Card>
          <div className="text-center">
            <Link
              to="/"
              className="text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
            >
              Back to home
            </Link>
          </div>
        </div>
      </main>
      {/* <Footer /> */}
    </div>
  );
}

export { Login as default };
